import { Reveal } from "../components/Reveal";
import { AboutTimeline } from "../components/AboutTimeline";
import { AdjectiveRibbon } from "../components/AdjectiveRibbon";
import petePortrait from "@/assets/pete-portrait.webp";
import { ArrowRight } from "lucide-react";

const TIMELINE = [
  {
    year: "Late '80s – early '90s",
    title: "2WayTrust",
    body: "Pete worked under the banner of 2WayTrust, always focusing on the importance of reciprocity in high-trust relationships among leaders – before turning to wider team development under the banner of \u201COpen Agenda\u201D.",
  },
  {
    year: "1987",
    title: "St George's House, Windsor Castle",
    body: "Appointed a Fellow of St George's House inside Windsor Castle, leading consultations on the future of citizenship, income and work.",
  },
  {
    year: "Post-9/11",
    title: "The New World Order Forum",
    body: "The whole focus of the New World Order Forum was on trust – or the lack of it – between different cultures and political systems at a global level, in the years immediately after the events of 9/11.",
  },
  {
    year: "2005 – 2015",
    title: "From policies to people",
    body: "During this period the balance of Pete's work shifted from running ideas-building sessions for Government Departments and think tanks to leadership development for Boards and top teams. As he puts it, it took him a few years to \u201Csee the light\u201D – and fully appreciate that there's little point having the \u201Cright policies\u201D if you don't have the \u201Cright leaders\u201D to bring them to life and make them real.",
  },
  {
    year: "2016",
    title: "The Society of Leadership Fellows",
    body: "Invited to become founding Director, Pete built a community of some 250 Leadership Fellows – CEOs, Chairs and established leaders – facilitating intensive one, two and three-day events.",
  },
  {
    year: "2020 \u2192 today",
    title: "Confidential troubleshooting",
    body: "Behind-the-scenes confidential troubleshooting for CEOs and Boards – focusing on conflict resolution, and on learning the positive lessons that need to be drawn when protracted disputes among top leaders finally come to an end. It is this work that shaped the Tough Trust Method.",
  },
];

export default function About() {
  return (
    <div data-testid="about-page">
      {/* STORY */}
      <section className="pt-32 md:pt-40 pb-24 md:pb-28 bg-ivory">
        <div className="container-px grid lg:grid-cols-12 gap-14 items-center">
          <Reveal className="lg:col-span-7 space-y-5 font-light leading-relaxed">
            <p className="overline">Pete's story</p>
            <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-ink leading-tight !mt-4">
              A career devoted to one idea.
            </h1>
            <p className="font-serif text-6xl md:text-7xl font-bold text-windsor leading-none !mt-6">
              Trust.
            </p>
            <p className="text-lg text-ink/80 max-w-xl !mt-6">
              Since he was first appointed as a Fellow of St George's House, Windsor Castle, in 1987,
              Pete has helped leaders and Boards build the kind of trust that is{" "}
              <span className="text-windsor font-medium italic">tough enough to hold under real pressure</span>.
            </p>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-5">
            <img
              src={petePortrait}
              alt="Portrait of Pete Ashby, leadership coach and creator of the Tough Trust Method"
              loading="lazy"
              className="w-full h-[620px] object-cover object-[50%_28%]"
            />
            <p className="text-ink/55 text-sm mt-3 italic">
              Pete Ashby – leadership adviser &amp; facilitator.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ADJECTIVE RIBBON */}
      <AdjectiveRibbon />

      {/* TIMELINE */}
      <section className="relative py-24 md:py-32 bg-white border-y border-ink/10 overflow-hidden">
        <img
          src="/tt-mark.png"
          alt=""
          aria-hidden
          data-testid="about-watermark"
          className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[480px] max-w-none opacity-[0.07] select-none"
        />
        <div className="container-px relative z-10">
          <AboutTimeline items={TIMELINE} />
        </div>
      </section>

      {/* BELIEF QUOTE */}
      <section className="py-24 md:py-32 bg-ink text-ivory">
        <div className="container-px max-w-4xl">
          <Reveal>
            <p className="overline !text-gold">The unifying word</p>
            <p className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-tight mt-6">
              "Trust that holds under pressure is built, not assumed – earned in the small decisions,
              every single day."
            </p>
            <p className="text-ivory/70 font-light mt-8 leading-relaxed max-w-2xl">
              It is the thread running through every banner Pete has worked under, and the foundation
              of everything he brings to the leaders and Boards he works with today.
            </p>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-windsor">
        <div className="container-px text-center">
          <Reveal>
            <p className="overline !text-ivory">Work with Pete</p>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-ivory mt-4 max-w-2xl mx-auto leading-tight">
              Bring 4 decades of boardroom facilitation and insights to your team.
            </h2>
            <a
              href="mailto:pete@peteashbycoaching.com"
              data-testid="about-cta-work"
              className="inline-flex items-center gap-2 mt-9 bg-ivory text-windsor px-8 py-4 font-medium hover:bg-ink hover:text-ivory transition-colors"
            >
              Work with Pete <ArrowRight size={18} />
            </a>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
